import React from 'react';
import type { SkillFamily as SkillFamilyType } from '@gloat/types';
import ProgressBar, { variants } from '../../components/progress-bar';
import Text, { TextProps } from '../../components/text/text';
import { Span, Flex } from '../../components/box';
import EllipsisWithTooltip from '../../components/ellipsis-with-tooltip';
import colors from '../../style/colors';

/******************
 * Label
 ******************/

export interface SkillLabelProps extends TextProps {
  maxWidth?: number | 'unset';
}

export const SkillLabel = ({ maxWidth: maxWidthProp = 240, ...rest }: SkillLabelProps) => {
  // On mobile, do not exceed 180px of width
  const maxWidth =
    window.innerWidth < 767 && typeof maxWidthProp === 'number' ? Math.min(maxWidthProp, 180) : maxWidthProp;

  if (maxWidth) {
    return (
      <EllipsisWithTooltip maxWidth={maxWidth}>
        <Text weight={500} variant={'gray900'} size={'inherit'} {...rest} />
      </EllipsisWithTooltip>
    );
  }

  return <Text weight={500} variant={'gray900'} size={'inherit'} {...rest} />;
};

/******************
 * Proficiency Bars
 ******************/
export interface SkillProficiencyBarsProps {
  levels: { index: number }[];
  skillProficiency: { level?: number; label?: string; variant?: keyof typeof variants }[];
  skillId?: string;
}

export const SkillProficiencyBars = ({ levels, skillProficiency, skillId }: SkillProficiencyBarsProps) => {
  return (
    <Span display={'flex'} flexDirection={'column'} gap={'tiny'} flex={'1 0 20px'}>
      {skillProficiency.map(skill => (
        <Span key={skill.variant} id={`proficiency-${skillId}-${skill.variant}`}>
                    <ProgressBar
            value={((skill.level || 0) * 100) / levels.length}
            progressVariant={skill.variant || 'sun'}
            size={'xs'}
            aria-label={skill.label || 'Skill proficiency'}
            style={{ border: `1px solid ${colors.gray600}`, height: 6 }}
          />
        </Span>
      ))}
    </Span>
  );
};

export type SkillCategoryProps = {
  skill_class: 'hard' | 'soft';
  hardLabel: React.ReactNode;
  softLabel: React.ReactNode;
};

export const SkillCategory = ({ skill_class, hardLabel, softLabel }: SkillCategoryProps) => {
  const skillCategoriesByClass: Record<'hard' | 'soft', React.ReactNode> = {
    hard: hardLabel || 'Skill',
    soft: softLabel || 'Behavior',
  };

  return (
    <Text size="sm" variant="gray600">
      ({skillCategoriesByClass[skill_class]})
    </Text>
  );
};

const familyTitles = ['Domain', 'Family', 'Sub-family'] as const;

export type SkillFamiliesProps = { families: SkillFamilyType[] };

export const SkillFamilies = ({ families }: SkillFamiliesProps) => {
  return (
    <Flex flexDirection="column" gap="sm" flex={1} overflowX={'hidden'}>
      {families.slice(0, 3).map((item, index) => (
        <Flex gap="sm" key={index} alignItems="center">
          {item.hierarchy.map((family, idx) => {
            const isLast = idx === item.hierarchy.length - 1;

            return (
              <Flex alignItems={'center'} key={family}>
                <Flex flexDirection="column" gap="xxs" marginRight={'xs'}>
                  <Text size="sm" variant="gray600" weight={500}>
                    {familyTitles[idx]}
                  </Text>
                  <Flex>
                    <Text size="sm" variant="gray600">
                      {family}
                    </Text>
                  </Flex>
                </Flex>
                <Text weight={500} variant={'gray900'} marginLeft={'auto'}>
                  {isLast ? '' : '>'}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      ))}
    </Flex>
  );
};
